// src/components/AudioList.tsx

import React, { useMemo, useState, useRef, useEffect } from 'react';
import { AudioWithUrl, AudioFocus, AudioFocusLabels } from '../types/audio.types';

interface AudioListProps {
  audios: AudioWithUrl[];
  selectedAudioId?: string;
  onSelectAudio: (audio: AudioWithUrl) => void;
  loading: boolean;
  selectionMode?: 'single' | 'multiple';
  selectedAudioIds?: string[];
  onToggleSelection?: (audioId: string) => void;
  loadingAudioIds?: Set<string>;
}

const AudioList: React.FC<AudioListProps> = ({
  audios,
  selectedAudioId,
  onSelectAudio,
  loading,
  selectionMode = 'single',
  selectedAudioIds = [],
  onToggleSelection,
  loadingAudioIds = new Set()
}) => {
  // Estado para altura redimensionável
  const [listHeight, setListHeight] = useState<number>(400);
  const [isResizing, setIsResizing] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  // Handler de redimensionamento
  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (!isResizing || !containerRef.current) return;

      const containerRect = containerRef.current.getBoundingClientRect();
      const newHeight = e.clientY - containerRect.top;

      // Limita altura mínima e máxima
      const minHeight = 200;
      const maxHeight = window.innerHeight - 200;

      if (newHeight >= minHeight && newHeight <= maxHeight) {
        setListHeight(newHeight);
      }
    };

    const handleMouseUp = () => {
      setIsResizing(false);
    };

    if (isResizing) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = 'ns-resize';
      document.body.style.userSelect = 'none';
    }

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };
  }, [isResizing]);

  // Agrupa áudios por paciente
  const groupedAudios = useMemo(() => {
    const grouped = new Map<string, AudioWithUrl[]>();
    
    // Primeira passagem: remover duplicatas e agrupar
    const seen = new Set<string>();
    for (const audio of audios) {
      if (!seen.has(audio.audioId)) {
        seen.add(audio.audioId);
        const patientAudios = grouped.get(audio.subject_id) || [];
        patientAudios.push(audio);
        grouped.set(audio.subject_id, patientAudios);
      }
    }
    
    // Log de duplicatas
    if (seen.size !== audios.length) {
      console.warn(
        `[AudioList] Removidas ${audios.length - seen.size} duplicatas. ` +
        `Total original: ${audios.length}, único: ${seen.size}`
      );
    }
    
    return grouped;
  }, [audios]);

  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow p-8 text-center">
        <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
        <p className="mt-4 text-sm text-gray-600">Carregando áudios...</p>
      </div>
    );
  }

  if (groupedAudios.size === 0) {
    return (
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-12 text-center">
        <svg
          className="mx-auto h-12 w-12 text-gray-400 mb-4"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
          />
        </svg>
        <h3 className="text-sm font-medium text-gray-900 mb-1">
          Nenhum áudio encontrado
        </h3>
        <p className="text-sm text-gray-600">
          Tente ajustar os filtros para encontrar áudios
        </p>
      </div>
    );
  }

  return (
    <div ref={containerRef} className="bg-white rounded-lg shadow-sm border border-gray-200 relative">
      {/* Header */}
      <div className="border-b border-gray-200 px-4 py-3">
        <div className="flex items-center justify-between">
          <h3 className="text-sm font-semibold text-gray-900">
            {selectionMode === 'multiple' ? 'Selecionar Áudios' : 'Lista de Pacientes'}
          </h3>
          <span className="text-sm text-gray-600">
            {groupedAudios.size} paciente{groupedAudios.size !== 1 ? 's' : ''}
          </span>
        </div>
      </div>

      {/* Patient List */}
      <div
        className="divide-y divide-gray-200 overflow-y-auto"
        style={{ height: `${listHeight}px` }}
      >
        {Array.from(groupedAudios.entries()).map(([patientId, patientAudios]) => {
          const hasSelectedAudio = patientAudios.some(
            audio => selectionMode === 'single' 
              ? audio.audioId === selectedAudioId 
              : selectedAudioIds.includes(audio.audioId)
          );

          return (
            <div
              key={patientId}
              className={`
                px-4 py-3 transition-colors
                ${hasSelectedAudio 
                  ? 'bg-indigo-50/50' 
                  : 'hover:bg-gray-50'
                }
              `}
            >
              {/* Patient Info Header */}
              <div className="space-y-3">
                <div className="flex items-start justify-between">
                  <div>
                    <div className="flex items-center gap-2 mb-2">
                      <h4 className="text-sm font-semibold text-gray-900">
                        Paciente {patientId}
                      </h4>
                      {patientAudios[0]?.biological_sex && (
                        <span className="text-xs px-2 py-0.5 bg-blue-100 text-blue-700 rounded-full">
                          {patientAudios[0].biological_sex}
                        </span>
                      )}
                      {patientAudios[0]?.outcome && (
                        <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                          patientAudios[0].outcome === 'Normal' ? 'bg-green-100 text-green-800' : 
                          patientAudios[0].outcome === 'Abnormal' ? 'bg-red-100 text-red-800' : 
                          'bg-gray-100 text-gray-800'
                        }`}>
                          {patientAudios[0].outcome}
                        </span>
                      )}
                    </div>

                    {/* Dados Demográficos */}
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-x-4 gap-y-1 text-xs text-gray-600">
                      {patientAudios[0]?.age_group && (
                        <div className="flex items-center gap-1">
                          <svg className="w-3.5 h-3.5 text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 15.546c-.523 0-1.046.151-1.5.454a2.704 2.704 0 01-3 0 2.704 2.704 0 00-3 0 2.704 2.704 0 01-3 0 2.704 2.704 0 00-3 0 2.704 2.704 0 01-3 0 2.701 2.701 0 00-1.5-.454M9 6v2m3-2v2m3-2v2M9 3h.01M12 3h.01M15 3h.01M21 21v-7a2 2 0 00-2-2H5a2 2 0 00-2 2v7h18zm-3-9v-2a2 2 0 00-2-2H8a2 2 0 00-2 2v2h12z" />
                          </svg>
                          <span>{patientAudios[0].age_group}</span>
                        </div>
                      )}
                      {patientAudios[0]?.height_cm && (
                        <div className="flex items-center gap-1">
                          <svg className="w-3.5 h-3.5 text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 9l4-4 4 4m0 6l-4 4-4-4" />
                          </svg>
                          <span>{patientAudios[0].height_cm} cm</span>
                        </div>
                      )}
                      {patientAudios[0]?.weight_kg && (
                        <div className="flex items-center gap-1">
                          <svg className="w-3.5 h-3.5 text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 6l3 1m0 0l-3 9a5.002 5.002 0 006.001 0M6 7l3 9M6 7l6-2m6 2l3-1m-3 1l-3 9a5.002 5.002 0 006.001 0M18 7l3 9m-3-9l-6-2m0-2v2m0 16V5m0 16H9m3 0h3" />
                          </svg>
                          <span>{patientAudios[0].weight_kg} kg</span>
                        </div>
                      )}
                      {patientAudios[0]?.pregnancy_status !== null && patientAudios[0]?.pregnancy_status !== undefined && (
                        <div className="flex items-center gap-1">
                          <svg className="w-3.5 h-3.5 text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                          <span>Gravidez: {patientAudios[0].pregnancy_status ? 'Sim' : 'Não'}</span>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Indicadores de Murmúrio */}
                  <div className="flex items-center gap-2">
                    {patientAudios.some(a => a.murmur === 'Present') && (
                      <span className="text-xs px-2 py-0.5 bg-orange-100 text-orange-800 rounded-full font-medium">
                        Com Murmúrio
                      </span>
                    )}
                  </div>
                </div>

                {/* Dados de Murmúrio */}
                {patientAudios.some(a => a.murmur === 'Present') && (
                  <div className="px-3 py-2 bg-orange-50 rounded-lg border border-orange-100">
                    <div className="text-xs text-orange-900">
                      {patientAudios[0]?.most_audible_location && (
                        <div className="mb-1">
                          <span className="font-medium">Local Mais Audível:</span> {patientAudios[0].most_audible_location}
                        </div>
                      )}
                      {patientAudios[0]?.murmur_locations && (
                        <div className="mb-1">
                          <span className="font-medium">Locais do Murmúrio:</span>
                          <div className="flex flex-wrap gap-1 mt-1">
                            {patientAudios[0].murmur_locations.split('+').map(location => {
                              const selectedAudio = selectedAudioId 
                                ? patientAudios.find(a => a.audioId === selectedAudioId)
                                : null;
                              
                              const isSelected = selectedAudio
                                ? AudioFocusLabels[selectedAudio.focus] === location
                                : false;
                              
                              return (
                                <span
                                  key={location}
                                  className={`text-xs px-2 py-0.5 rounded-full cursor-pointer transition-colors
                                    ${isSelected
                                      ? 'bg-indigo-100 text-indigo-700 font-medium'
                                      : 'bg-gray-100 text-gray-700 hover:bg-indigo-50'}`}
                                  onClick={() => {
                                    const audioWithLocation = patientAudios.find(
                                      a => AudioFocusLabels[a.focus] === location
                                    );
                                    if (audioWithLocation) {
                                      onSelectAudio(audioWithLocation);
                                    }
                                  }}
                                >
                                  {location}
                                </span>
                              );
                            })}
                          </div>
                        </div>
                      )}
                      <div className="grid grid-cols-2 sm:grid-cols-3 gap-x-4 gap-y-1 mt-2">
                        {patientAudios[0]?.systolic_murmur_timing && (
                          <div><span className="font-medium">Timing:</span> {patientAudios[0].systolic_murmur_timing}</div>
                        )}
                        {patientAudios[0]?.systolic_murmur_shape && (
                          <div><span className="font-medium">Shape:</span> {patientAudios[0].systolic_murmur_shape}</div>
                        )}
                        {patientAudios[0]?.systolic_murmur_grading && (
                          <div><span className="font-medium">Grading:</span> {patientAudios[0].systolic_murmur_grading}</div>
                        )}
                        {patientAudios[0]?.systolic_murmur_pitch && (
                          <div><span className="font-medium">Pitch:</span> {patientAudios[0].systolic_murmur_pitch}</div>
                        )}
                        {patientAudios[0]?.systolic_murmur_quality && (
                          <div><span className="font-medium">Quality:</span> {patientAudios[0].systolic_murmur_quality}</div>
                        )}
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Focus Grid */}
              <div className="grid grid-cols-5 gap-2">
                {[AudioFocus.AORTIC, AudioFocus.PULMONIC, AudioFocus.MITRAL, AudioFocus.TRICUSPID, AudioFocus.PHC].map(focus => {
                  const audio = patientAudios.find(a => a.focus === focus);
                  const isSelected = audio && (
                    selectionMode === 'single'
                      ? audio.audioId === selectedAudioId
                      : selectedAudioIds.includes(audio.audioId)
                  );
                  const isLoading = audio && loadingAudioIds.has(audio.audioId);

                  return (
                    <button
                      key={focus}
                      onClick={() => {
                        if (audio && !isLoading) {
                          if (selectionMode === 'single') {
                            onSelectAudio(audio);
                          } else if (onToggleSelection) {
                            onToggleSelection(audio.audioId);
                          }
                        }
                      }}
                      disabled={!audio || isLoading}
                      className={`
                        p-2 rounded-lg text-sm relative
                        ${audio 
                          ? isSelected
                            ? 'bg-indigo-100 border-2 border-indigo-600 text-indigo-700'
                            : 'bg-gray-50 border border-gray-300 hover:bg-gray-100'
                          : 'bg-gray-50 border border-gray-200 opacity-50 cursor-not-allowed'
                        }
                        ${isLoading ? 'cursor-wait' : audio ? 'cursor-pointer' : 'cursor-not-allowed'}
                        min-h-[64px] flex flex-col items-center justify-center gap-1
                      `}
                      title={audio ? `Selecionar foco ${AudioFocusLabels[audio.focus]}` : `Nenhum áudio para ${AudioFocusLabels[focus]}`}
                    >
                      <span className="font-medium">{AudioFocusLabels[focus]}</span>
                      {audio && (
                        <>
                          {audio.murmur && audio.murmur !== 'Unknown' && (
                            <span className={`text-xs px-1.5 py-0.5 rounded ${
                              audio.murmur === 'Present' ? 'bg-orange-100 text-orange-800' : 
                              'bg-blue-100 text-blue-800'
                            }`}>
                              {audio.murmur}
                            </span>
                          )}
                          {isLoading && (
                            <div className="absolute inset-0 bg-white bg-opacity-50 flex items-center justify-center">
                              <div className="w-4 h-4 border-2 border-indigo-600 border-t-transparent rounded-full animate-spin"></div>
                            </div>
                          )}
                          {selectionMode === 'multiple' && (
                            <div className="absolute top-1 right-1">
                              <input
                                type="checkbox"
                                checked={isSelected}
                                onChange={(e) => {
                                  e.stopPropagation();
                                  if (onToggleSelection && !isLoading) {
                                    onToggleSelection(audio.audioId);
                                  }
                                }}
                                disabled={isLoading}
                                className="h-3 w-3 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded cursor-pointer"
                              />
                            </div>
                          )}
                        </>
                      )}
                    </button>
                  );
                })}
              </div>

              {/* Audio Details */}
              <div className="mt-2">
                {patientAudios.map(audio => {
                  const isSelected = selectionMode === 'single' 
                    ? audio.audioId === selectedAudioId 
                    : selectedAudioIds.includes(audio.audioId);

                  if (isSelected) {
                    return (
                      <div 
                        key={audio.audioId}
                        className="text-xs p-2 bg-indigo-50 rounded border border-indigo-100"
                      >
                        <div className="flex items-center gap-2 mb-1">
                          <span className="font-medium">{AudioFocusLabels[audio.focus]}</span>
                          <span className="text-gray-600">({audio.audioId})</span>
                        </div>

                        {/* Murmur Details */}
                        {(audio.systolic_murmur_grading || audio.systolic_murmur_timing) && (
                          <div className="text-gray-700">
                            <span className="font-medium">Murmúrio Sistólico:</span>
                            {audio.systolic_murmur_grading && (
                              <span className="ml-1">{audio.systolic_murmur_grading}</span>
                            )}
                            {audio.systolic_murmur_timing && (
                              <span className="ml-1">({audio.systolic_murmur_timing})</span>
                            )}
                            {audio.systolic_murmur_quality && (
                              <span className="ml-1">- {audio.systolic_murmur_quality}</span>
                            )}
                          </div>
                        )}

                        {/* Current Label */}
                        {audio.current_label && (
                          <div className="mt-1 text-gray-700">
                            <span className="font-medium">Rótulo:</span>
                            <span className="ml-1 text-indigo-600">{audio.current_label.label}</span>
                            {audio.current_label.comment && (
                              <p className="mt-0.5 text-gray-500">{audio.current_label.comment}</p>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  }
                  return null;
                })}
              </div>
            </div>
          );
        })}
      </div>

      {/* Resize Handle */}
      <div
        className={`
          absolute bottom-0 left-0 right-0 h-2 cursor-ns-resize
          flex items-center justify-center
          hover:bg-indigo-100 transition-colors
          group
          ${isResizing ? 'bg-indigo-200' : 'bg-transparent'}
        `}
        onMouseDown={(e) => {
          e.preventDefault();
          setIsResizing(true);
        }}
        title="Arraste para redimensionar"
      >
        <div className="w-12 h-1 bg-gray-300 rounded-full group-hover:bg-indigo-400 transition-colors" />
      </div>
    </div>
  );
};

export default AudioList;
